/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { Switch, Route } from "react-router-dom";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Color from "color";
import get from "lodash/get";
import ImagePalette from "react-image-palette";
import { useSegmentClient } from "@redbubble/react-segment";
import * as constants from "@redbubble/design-system/react/constants";
import PageSection from "@redbubble/design-system/react/PageSection";
import ChevronLeftIcon from "@redbubble/design-system/react/Icons/ChevronLeft";
import Box from "@redbubble/design-system/react/Box";
import TextLink from "@redbubble/design-system/react/TextLink";
import Card from "@redbubble/design-system/react/Card";
import Modal from "@redbubble/design-system/react/Modal";
import Image from "@redbubble/design-system/react/Image";
import Text from "@redbubble/design-system/react/Text";
import { SMALL, LARGE } from "@redbubble/design-system/react/constants";
import Avatar from "@redbubble/design-system/react/Avatar";
import AvailableProductsSection from "./components/AvailableProductsSection";
import CommentsSection from "./components/CommentsSection";
import useAvailableProducts from "../../../hooks/apollo/useAvailableProducts";
import { DEFAULT_PREVIEW, PRODUCT_CLOSE_PREVIEW } from "../../../lib/previews";
import config from "../../../../config";
import Heart from "../../Lists/Heart";
import ShareButton from "../../ShareButton";
import { browserPropType } from "../../../containers/redux/withBrowserInfo";
import { userInfoPropType } from "../../../containers/apollo/withUserInfo";
import makeWorkThumbnail from "./makeWorkThumbnail";
import Metadata from "../../Metadata";
import styles from "./RangePage.scss";
import { analyticsPayload } from "../../../lib/analytics";
import { localizedPath } from "../../../lib/routing";
import messages from "./messages";
import { listManagerShape } from "../../Lists";
import { useComments } from "../../../containers/apollo/useComments";
import { ImageProtectContainer } from "../../ImageProtection";
import TabNav from "./components/TabNav";
import placeholder from "./placeholder.png";
import { SegmentPages } from "../../../consts";
import { intlPropType } from "../../../lib/propTypes";

const MAX_DESCRIPTION_LENGTH = 140;

const trunc = (str, n) => {
  if (!str) return str;

  return str.length <= n ? (
    str
  ) : (
    <span className={styles.longDescription}>
      {str.slice(0, n)}...{" "}
      <TextLink>
        <FormattedMessage defaultMessage="Read more" />
      </TextLink>
    </span>
  );
};

const useConfiguredAvailableProducts = ({ workId, userInfo, ssr = true }) => {
  return useAvailableProducts({
    workId,
    userInfo,
    previewTypeIds: [DEFAULT_PREVIEW, PRODUCT_CLOSE_PREVIEW],
    ssr,
  });
};

const RangePage = (props) => {
  const {
    validateAndSetDimensions,
    logPageView,
    setGAClientOptions,
    intl,
    logEvent,
    browser,
    work,
    workLoading,
    artistInfo,
    artistInfoLoading,
    artistName,
    userInfo,
    userInfoLoading,
    listManager,
    openLoginSignupModal,
  } = props;

  const workId = get(work, "id");

  const { availableProducts, availableProductsLoading } =
    useConfiguredAvailableProducts({
      userInfo,
      workId,
    });

  useEffect(() => {
    validateAndSetDimensions();
    setGAClientOptions({ location: window.location.href });
    logPageView();
  }, [validateAndSetDimensions, logPageView, setGAClientOptions]);

  const [bgColors, setBgColors] = useState(null);

  const { comments, commentsLoading, commentsError } = useComments({ workId });

  // eslint-disable-next-line react/prop-types
  const isLarge = get(props, "browser.is.large") || false;
  const locale = get(props, "userInfo.locale") || "en";
  const isLoggedIn = get(userInfo, "isLoggedIn", false);
  const description = get(work, "description");
  const title = get(work, "title");
  const remoteKey = get(work, "remoteKey");
  const artistId = get(work, "artistId");
  const shopUrl = get(work, "shopUrl");
  const avatarUrl = get(artistInfo, "avatar");
  const displayName = get(artistInfo, "shopDisplayName");
  const worksCount = get(artistInfo, "worksCount");

  const segmentClient = useSegmentClient();
  const currency = get(userInfo, "currency", null);
  const country = get(userInfo, "country", null);

  useEffect(() => {
    segmentClient.page(SegmentPages.WORK, {
      locale,
      currency,
      country,
      work_id: workId,
    });
  }, [workId]);

  const isArtist = get(userInfo, "userId") === artistId;
  let shareUrl = `${config("rbmBaseWebUrl")}${get(props, "match.url")}`;

  if (isArtist) {
    shareUrl += "?asc=u";
  }

  const shareTitle = intl.formatMessage(messages.workShareTitle, {
    title,
    name: displayName,
  });

  let thumbnail;

  if (workId && remoteKey) {
    thumbnail = makeWorkThumbnail(remoteKey, workId);
  }

  const cardWrap = (component, wrap) => {
    if (wrap) {
      return (
        <Card elevation={constants.ELEVATION_LOW} roundedCorners={false}>
          {component}
        </Card>
      );
    }
    return component;
  };

  const getAdjustedColors = () => {
    if (!bgColors || bgColors.default) {
      return { backgroundColor: "#ffffff", textColor: "inherit" };
    }

    const tweakColor = (rgb, saturation, luminosity) => {
      const rgbColor = Color(rgb);
      const hsl = rgbColor.hsl().array();
      const transformedHsl = `hsl(${hsl[0]},${saturation}%,${luminosity}%)`;
      return transformedHsl;
    };

    const { backgroundColor } = bgColors;
    return {
      backgroundColor: tweakColor(backgroundColor, 86, 96),
      textColor: tweakColor(backgroundColor, 95, 14),
    };
  };

  const navSection = (textColor) => {
    const exploreUrl = `${config(
      "rbmBaseWebUrl"
    )}/people/${artistName}/explore`;
    return (
      <Box
        className={styles.navContainer}
        display="flex"
        paddingTop={isLarge ? 2 : 0.5}
        paddingBottom={isLarge ? 2 : 0.5}
      >
        <a
          href={exploreUrl}
          onClick={() => {
            logEvent({
              analytics: analyticsPayload.artistWorkClickBackToExplore(
                workId,
                exploreUrl
              ),
            });
          }}
          className={styles.navLink}
          style={{
            color: textColor,
            maxWidth: "calc(100% - 80px)",
          }}
        >
          {avatarUrl && (
            <Box display="flex" alignItems="center">
              <ChevronLeftIcon size={LARGE} />
              <Avatar
                loading={artistInfoLoading}
                src={avatarUrl}
                size={SMALL}
                bordered={{ color: "var(--ds-color-background-ui)" }}
              />
            </Box>
          )}
          <Box marginLeft="m">
            {/* eslint-disable-next-line @redbubble/design-system/no-style-overrides */}
            <Text
              display="block"
              type="display3"
              loading={artistInfoLoading}
              style={{ color: textColor }}
              element="h3"
            >
              <strong>{displayName}</strong>
            </Text>
            {/* eslint-disable-next-line @redbubble/design-system/no-style-overrides */}
            <Text
              type="body"
              loading={artistInfoLoading}
              style={{ color: textColor }}
            >
              {intl.formatMessage(messages.totalWorks, {
                worksCount,
              })}
            </Text>
          </Box>
        </a>
      </Box>
    );
  };

  const artworkSection = (backgroundColor) => {
    const { backgroundColor: hsl, textColor } =
      getAdjustedColors(backgroundColor);
    return (
      <PageSection backgroundColor={hsl}>
        {navSection(textColor)}
        <Box className={styles.workContainer} display="flex">
          <Box
            className={styles.work}
            marginRight={isLarge ? 1.5 : 0.75}
            style={{ opacity: workLoading ? 0 : 1 }}
            display="flex"
          >
            <ImageProtectContainer>
              {cardWrap(
                <Image
                  fluid
                  src={thumbnail}
                  style={{ width: "100%", height: "100%" }}
                  loadOnVisible={{
                    placeholder,
                  }}
                />,
                isLarge
              )}
            </ImageProtectContainer>
          </Box>
          <Box
            className={styles.workInfo}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            {/* eslint-disable-next-line @redbubble/design-system/no-style-overrides */}
            <Text
              display="block"
              element="h1"
              type={isLarge ? "display1" : "display3"}
              loading={workLoading}
              style={{
                color: textColor,
                maxWidth: "calc(100% - 80px)",
              }}
            >
              {workLoading ? "Work title goes here" : title}
            </Text>
            {/* eslint-disable-next-line @redbubble/design-system/no-style-overrides */}
            <Text
              display="block"
              type={isLarge ? "display4" : "body2"}
              muted
              loading={workLoading}
              style={{
                color: textColor,
                maxWidth: "calc(100% - 80px)",
              }}
            >
              {workLoading ? (
                "Artist credit here"
              ) : (
                <FormattedMessage
                  defaultMessage="by&nbsp;{name}"
                  values={{
                    name: shopUrl ? (
                      <TextLink href={shopUrl} type="display4">
                        {displayName}
                      </TextLink>
                    ) : (
                      displayName
                    ),
                  }}
                />
              )}
            </Text>
            {!workLoading && (
              <Box
                style={{
                  maxWidth: 768,
                }}
                marginTop="xs"
              >
                {description && description.length > MAX_DESCRIPTION_LENGTH ? (
                  <Modal
                    accessibleTitle="Full description"
                    trigger={({ onClick }) => (
                      <Box
                        className={styles.description}
                        style={{ color: textColor }}
                      >
                        <Text display="block" onClick={onClick}>
                          {trunc(description, MAX_DESCRIPTION_LENGTH)}
                        </Text>
                      </Box>
                    )}
                  >
                    <Box padding="m">
                      <Box display="flex" marginBottom="l">
                        <Box marginRight="s">
                          <Image
                            size={constants.SMALL}
                            src={thumbnail}
                            roundedCorners
                          />
                        </Box>
                        <Box style={{ color: textColor }}>
                          <Text
                            display="block"
                            type={isLarge ? "display3" : "display5"}
                          >
                            {title}
                          </Text>
                          {/* eslint-disable-next-line @redbubble/design-system/no-style-overrides */}
                          <Text
                            display="block"
                            type={isLarge ? "body" : "body2"}
                            style={{ color: textColor }}
                          >
                            <FormattedMessage
                              defaultMessage="by&nbsp;{name}"
                              values={{
                                name: shopUrl ? (
                                  <TextLink href={shopUrl}>
                                    {displayName}
                                  </TextLink>
                                ) : (
                                  displayName
                                ),
                              }}
                            />
                          </Text>
                        </Box>
                      </Box>
                      <Box
                        className={styles.longDescription}
                        style={{ color: textColor }}
                      >
                        <Text display="block">{description}</Text>
                      </Box>
                    </Box>
                  </Modal>
                ) : (
                  <Box
                    style={{
                      maxWidth: 370,
                      color: textColor,
                    }}
                  >
                    {description}
                  </Box>
                )}
              </Box>
            )}
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              marginTop="m"
              className={styles.buttonsContainer}
            >
              <Box>
                <Heart
                  item={{
                    entityType: "WORK",
                    entityId: workId,
                    preview: thumbnail,
                  }}
                  isLoggedIn={isLoggedIn}
                  locale={locale}
                  listManager={listManager}
                  openLoginSignupModal={openLoginSignupModal}
                  noDropShadow
                  size={constants.MEDIUM}
                />
              </Box>
              <Box>
                <ShareButton
                  size={constants.MEDIUM}
                  shareUrl={shareUrl}
                  shareTitle={shareTitle}
                  shareDescription={description}
                  logEvent={() =>
                    logEvent({
                      analytics: analyticsPayload.artistWorkShareClicked(),
                    })
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </PageSection>
    );
  };

  return (
    <Box style={{ backgroundColor: "var(--ds-color-white)" }}>
      <Metadata
        image={thumbnail}
        path={get(props, "match.url")}
        title={shareTitle}
        description={description}
        noIndex
        includeFacebook
        includeTwitter
      />
      {artworkSection()}
      {!browser.is.small && (
        <ImagePalette
          defaults={{ default: true }}
          crossOrigin=""
          image={thumbnail}
        >
          {(backgroundColor) => {
            setBgColors(backgroundColor);
            return null;
          }}
        </ImagePalette>
      )}
      <Box className={styles.tabPadding} />
      <TabNav
        commentsCount={comments.length}
        currentPath={get(props, "location.pathname")}
        locale={locale}
        productsCount={availableProducts.length}
        workId={workId}
      />
      <PageSection>
        <Switch>
          <Route exact path={localizedPath(locale, `/shop/ap/${workId}`)}>
            <AvailableProductsSection
              availableProducts={availableProducts}
              availableProductsLoading={availableProductsLoading}
              isLarge={isLarge}
              isLoggedIn={isLoggedIn}
              listManager={listManager}
              locale={locale}
              openLoginSignupModal={openLoginSignupModal}
            />
          </Route>
          <Route path={localizedPath(locale, `/shop/ap/${workId}/comments`)}>
            <CommentsSection
              artistId={artistId}
              comments={comments}
              commentsError={commentsError}
              commentsLoading={commentsLoading}
              isLoggedIn={isLoggedIn}
              locale={locale}
              logEvent={logEvent}
              productsCount={availableProducts.length}
              openLoginSignupModal={openLoginSignupModal}
              isArtist={isArtist}
              userAccount={get(props, "userAccount")}
              userAccountLoading={get(props, "userAccountLoading")}
              userInfo={userInfo}
              userInfoLoading={userInfoLoading}
              workId={workId}
            />
          </Route>
        </Switch>
      </PageSection>
    </Box>
  );
};

RangePage.propTypes = {
  browser: browserPropType,
  validateAndSetDimensions: PropTypes.func,
  logPageView: PropTypes.func,
  setGAClientOptions: PropTypes.func,
  userInfo: userInfoPropType,
  intl: intlPropType.isRequired,
  logEvent: PropTypes.func.isRequired,
  work: PropTypes.shape({
    description: PropTypes.string,
    title: PropTypes.string,
    remoteKey: PropTypes.string,
    artistId: PropTypes.string,
    name: PropTypes.string,
    shopUrl: PropTypes.string,
    workLoading: PropTypes.bool,
  }),
  workLoading: PropTypes.bool,
  artistInfo: PropTypes.shape({}).isRequired,
  artistInfoLoading: PropTypes.bool,
  artistName: PropTypes.string,
  listManager: listManagerShape,
  openLoginSignupModal: PropTypes.func,
  userInfoLoading: PropTypes.bool,
};

RangePage.defaultProps = {
  browser: null,
  workLoading: false,
  artistInfoLoading: true,
  artistName: "",
  userInfo: {},
  logPageView: () => {},
  setGAClientOptions: () => {},
  validateAndSetDimensions: () => {},
  work: {},
  listManager: null,
  openLoginSignupModal: () => {},
  userInfoLoading: true,
};

RangePage.displayName = "RangePage";

export default RangePage;
